import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Search, Users, Ban, CheckCircle2, Globe, Instagram, MessageCircle, Camera, MoreHorizontal } from "lucide-react";
import { useApp } from "@/contexts/AppContext";
import { formatSAR } from "@/lib/i18n";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const channelIcon = (c) => {
  switch (c) {
    case "instagram": return Instagram;
    case "whatsapp": return MessageCircle;
    case "snapchat": return Camera;
    default: return Globe;
  }
};

export default function Customers() {
  const { t, lang } = useApp();
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, banned: 0 });
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get(`/customers?status=${filter}${q ? `&q=${encodeURIComponent(q)}` : ""}`),
        api.get(`/customers/stats`),
      ]);
      setCustomers(c.data || []);
      setStats(s.data || { total: 0, active: 0, banned: 0 });
    } catch (e) {
      toast.error("Failed to load customers");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    const tid = setTimeout(fetchData, 300);
    return () => clearTimeout(tid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const updateStatus = async (id, newStatus) => {
    try {
      await api.patch(`/customers/${id}/status`, { status: newStatus });
      toast.success(lang === "ar" ? "تم التحديث" : "Updated");
      fetchData();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const tabs = [
    { key: "active", label: t.active, count: stats.active },
    { key: "banned", label: t.banned, count: stats.banned },
    { key: "all", label: t.all, count: stats.total },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground font-bold">{t.dashboard}</p>
          <h1 className="text-2xl sm:text-3xl font-bold mt-1">{t.customerList}</h1>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label={t.statTotalCustomers} value={stats.total} icon={<Users className="h-4 w-4" />} />
        <StatCard label={t.statActive} value={stats.active} icon={<CheckCircle2 className="h-4 w-4 text-success" />} />
        <StatCard label={t.statBanned} value={stats.banned} icon={<Ban className="h-4 w-4 text-destructive" />} />
      </div>

      {/* Filter tabs + search */}
      <div className="bg-surface border border-border rounded-xl overflow-hidden">
        <div className="flex items-center justify-between border-b border-border px-4 py-3 flex-wrap gap-3">
          <div className="inline-flex bg-muted rounded-lg p-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                data-testid={`customer-filter-${tab.key}`}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                  filter === tab.key ? "bg-surface text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {tab.label}
                <span className="text-[10px] bg-background border border-border px-1.5 py-0.5 rounded-full">
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              data-testid="customer-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t.search}
              className="ps-9"
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50">
              <tr className="text-text-muted text-start">
                <Th>{t.name}</Th>
                <Th>{t.mobile}</Th>
                <Th>{t.city}</Th>
                <Th>{t.channel}</Th>
                <Th>{t.totalOrders}</Th>
                <Th>{t.totalSpent}</Th>
                <Th>{t.status}</Th>
                <Th className="text-end">{t.actions}</Th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-muted-foreground">Loading...</td>
                </tr>
              )}
              {!loading && customers.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-10 text-muted-foreground" data-testid="no-customers">
                    {lang === "ar" ? "لا يوجد عملاء" : "No customers found"}
                  </td>
                </tr>
              )}
              {customers.map((c) => {
                const ChannelIcon = channelIcon(c.channel);
                return (
                  <tr
                    key={c.id}
                    data-testid={`customer-row-${c.id}`}
                    className="border-t border-border hover:bg-muted/30 transition-colors"
                  >
                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-full bg-accent text-accent-foreground flex items-center justify-center text-xs font-bold uppercase">
                          {c.name.split(" ").slice(0, 2).map((n) => n[0]).join("")}
                        </div>
                        <div>
                          <div className="font-medium">{c.name}</div>
                          <div className="text-xs text-muted-foreground">{c.email}</div>
                        </div>
                      </div>
                    </Td>
                    <Td className="font-mono text-xs">{c.mobile}</Td>
                    <Td>{c.city}</Td>
                    <Td>
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground capitalize">
                        <ChannelIcon className="h-3.5 w-3.5" />
                        {c.channel}
                      </span>
                    </Td>
                    <Td>{c.total_orders}</Td>
                    <Td className="font-semibold">{formatSAR(c.total_spent, lang)}</Td>
                    <Td>
                      <Badge
                        variant="outline"
                        className={
                          c.status === "active"
                            ? "border-success/30 text-success bg-success/5"
                            : "border-destructive/30 text-destructive bg-destructive/5"
                        }
                      >
                        {c.status === "active" ? t.active : t.banned}
                      </Badge>
                    </Td>
                    <Td className="text-end">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`customer-actions-${c.id}`}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {c.status === "active" ? (
                            <DropdownMenuItem
                              data-testid={`customer-ban-${c.id}`}
                              onClick={() => updateStatus(c.id, "banned")}
                              className="text-destructive"
                            >
                              <Ban className="h-4 w-4 me-2" />
                              {t.ban}
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              data-testid={`customer-unban-${c.id}`}
                              onClick={() => updateStatus(c.id, "active")}
                            >
                              <CheckCircle2 className="h-4 w-4 me-2" />
                              {t.unban}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`text-start font-medium text-muted-foreground py-3 px-4 text-xs uppercase tracking-wider ${className}`}>
    {children}
  </th>
);
const Td = ({ children, className = "" }) => (
  <td className={`py-3 px-4 align-middle ${className}`}>{children}</td>
);

const StatCard = ({ label, value, icon }) => (
  <div className="bg-surface border border-border rounded-xl p-5">
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted-foreground font-bold">
      {icon}
      {label}
    </div>
    <div className="text-3xl font-bold mt-2">{value}</div>
  </div>
);
